import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { BusinessHours } from '../../models/serializable/business-hours.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class BusinessHoursComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'fa15528bac2b41539a9b5738627f5f34';
  static readonly SCHEMA_VERSION: number = 2;
  static readonly DEFAULT_RENDER_ORDER: number = 4;

  constructor(
    public readonly businessHours: BusinessHours,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessHoursComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(BusinessHoursComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static fromBusinessHours(businessHours: BusinessHours, isRenderingEnabled: boolean): BusinessHoursComponent {
    return new BusinessHoursComponent(
        businessHours,
        BusinessHoursComponent.DEFAULT_RENDER_ORDER,
        isRenderingEnabled,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessHoursComponent {
    return new BusinessHoursComponent(
        context.fromData<BusinessHours>(MapUtils.getRaw(map, 'businessHours') as SerializedData),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessHours': context.toData(this.businessHours),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new BusinessHoursComponent(
        this.businessHours.clone(),
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: BusinessHoursComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.businessHours, this.businessHours) &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class BusinessHoursComponentBuilder extends DataModelBuilder<BusinessHoursComponent> {
  build(context: SerializationContext, map: SerializedData): BusinessHoursComponent {
    return BusinessHoursComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(BusinessHoursComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessHoursComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', BusinessHoursComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
